import type { BenchmarkResult } from './runBenchmark';

interface BenchmarkResultsTableProps {
  results: readonly BenchmarkResult[];
}

const LABELS: Record<BenchmarkResult['detector'], string> = {
  qr: 'QR',
  aruco_4x4_1000: 'ArUco 4×4 (1000)',
  text: 'Text (OCR)',
};

export function BenchmarkResultsTable({ results }: BenchmarkResultsTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-gray-700" data-testid="benchmark-results">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-gray-800 text-gray-300">
          <tr>
            <th className="px-3 py-2 text-left">Detector</th>
            <th className="px-3 py-2 text-right">Frames</th>
            <th
              className="px-3 py-2 text-right"
              title="Frames processed divided by pure detector time. Excludes seek + draw harness overhead."
            >
              Detector FPS
            </th>
            <th
              className="px-3 py-2 text-right"
              title="Total wall-clock time for the whole run. Dominated by seek harness cost, not a device throughput number."
            >
              Wall (s)
            </th>
            <th className="px-3 py-2 text-right">Hits</th>
            <th className="px-3 py-2 text-left">First 20 hits (value @ frame)</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => {
            const preview = r.hits
              .slice(0, 20)
              .map((h) => `${h.value}@${h.firstSeenFrame}`)
              .join(', ');
            return (
              <tr key={r.detector} className="border-t border-gray-700">
                <td className="px-3 py-2 font-medium text-gray-200">
                  {LABELS[r.detector]}
                  {r.cappedAtMax && (
                    <span
                      className="ml-2 rounded bg-amber-900/60 px-2 py-0.5 text-xs text-amber-200"
                      title="Video processing hit the maxSeconds cap"
                    >
                      capped
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.framesProcessed}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.avgDetectorFps}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {(r.wallTimeMs / 1000).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{r.hits.length}</td>
                <td className="px-3 py-2 text-gray-400">
                  {preview}
                  {r.hits.length > 20 ? ` … +${r.hits.length - 20}` : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-gray-500">
        Detector FPS is the honest device-throughput number. Wall (s) includes seek + draw harness
        overhead and doesn't reflect what a phone could achieve at scale.
      </p>
    </div>
  );
}
